import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  Container,
  Grid, // Используем Grid (v5) для основного макета
  Paper,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress, // Для индикатора загрузки
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { LangContext } from "../../LanguageProvider";
import InstagramFeed from "./instafeeds";
import useAuthStore from "../../services/AuthStore"; // <-- 1. Импорт твоего Store
import { useNavigate } from "react-router-dom";

// --- Твои стилизованные компоненты (Без изменений) ---
const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 360,
  height: "420px",
  transition: "transform 0.4s ease-in-out, box-shadow 0.4s ease-in-out",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: theme.shadows[8],
    cursor: "pointer",
  },
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
}));

const StyledCardMedia = styled(CardMedia)(({ theme }) => ({
  height: 220,
  transition: "opacity 0.4s ease-in-out, transform 0.4s ease-in-out",
  "&:hover": {
    opacity: 0.9,
    transform: "scale(1.02)",
  },
}));

const GradientOverlay = styled(Box)(({ theme }) => ({
  /* ... (твой стиль) ... */
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  height: "100%",
  background:
    "linear-gradient(180deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.8) 100%)",
}));

const StyledButton = styled(Button)(({ theme }) => ({
  /* ... (твой стиль) ... */
  marginTop: theme.spacing(2),
  background: "linear-gradient(to right, #38a169, #4299e1)",
  color: theme.palette.common.white,
  "&:hover": {
    background: "linear-gradient(to right, #38a169, #4299e1)",
    transform: "scale(1.03)",
  },
}));
// --- Конец стилизованных компонентов ---

// (Вставь сюда утилиту formatMonthYear из Шага 1)
const formatMonthYear = (isoMonth, lang = "ru") => {
  const [year, month] = isoMonth.split("-");
  const dateObj = new Date(year, month - 1);
  const options = { year: "numeric", month: "long" };
  const locale = lang === "kz" ? "kk-KZ" : "ru-RU";
  const formatted = new Intl.DateTimeFormat(locale, options).format(dateObj);
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

export default function News() {
  const { translations, lang } = useContext(LangContext); // <-- 2. Получаем 'lang'
  const { getNews } = useAuthStore(); // <-- 3. Получаем API из Store
  const navigate = useNavigate();

  // --- 4. Управление состоянием ---
  const [allNews, setAllNews] = useState([]); // Все новости (отсортированные)
  const [filteredNews, setFilteredNews] = useState([]); // Новости для показа (после фильтра)
  const [dateFilters, setDateFilters] = useState([]); // Уникальные месяцы (для кнопок)
  const [selectedFilter, setSelectedFilter] = useState(null); // Какой фильтр активен
  const [isLoading, setIsLoading] = useState(true);

  // --- 5. Загрузка и сортировка данных при монтировании ---
  useEffect(() => {
    const fetchNews = async () => {
      try {
        setIsLoading(true);
        // (Убедись, что твой getNews() в Store возвращает ВСЕ новости, а не 1 страницу)
        // (Возможно, тебе нужно обновить getNews(), чтобы он принимал { page: 1, per_page: 999 })
        const newsList = await getNews();

        // СОРТИРОВКА (новые вверху)
        const sortedNews = newsList.sort(
          (a, b) => new Date(b.public_date) - new Date(a.public_date),
        );

        setAllNews(sortedNews);
        setFilteredNews(sortedNews); // Сначала показываем все

        // ГЕНЕРАЦИЯ ФИЛЬТРОВ: Создаем уникальный список "Год-Месяц" (напр. "2025-09")
        const uniqueMonths = [
          ...new Set(sortedNews.map(n => n.public_date.substring(0, 7))),
        ];
        setDateFilters(uniqueMonths);
      } catch (error) {
        console.error("Ошибка при загрузке новостей:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNews();
  }, [getNews]); // Зависимость от getNews (из store)

  const truncateText = (text, limit) => {
    if (!text) return ""; // Защита от undefined
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  // --- 6. Логика фильтрации ---
  // Этот useEffect срабатывает, когда пользователь нажимает на фильтр
  useEffect(() => {
    if (selectedFilter === null) {
      setFilteredNews(allNews); // Если "Все новости", показываем все
    } else {
      // Иначе фильтруем master-список по "YYYY-MM"
      setFilteredNews(
        allNews.filter(news => news.public_date.startsWith(selectedFilter)),
      );
    }
  }, [selectedFilter, allNews]); // Пересчитываем, если изменился фильтр или сам список

  return (
    <Container
      maxWidth="lg"
      sx={{
        padding: 4,
        marginTop: 11,
        // (убраны flex-свойства отсюда, они больше не нужны)
      }}>
      <Typography
        variant="h4"
        gutterBottom
        align="center"
        sx={{ fontWeight: "bold", paddingTop: 5, color: "text.primary" }}>
        {translations["news"] || "Новости"} {/* <-- Мультиязычность */}
      </Typography>

      <InstagramFeed />

      {/* --- 7. НОВЫЙ МАКЕТ (2 КОЛОНКИ) --- */}
      <Grid container spacing={4} sx={{ paddingTop: 5 }}>
        {/* КОЛОНКА 1: ФИЛЬТРЫ ПО ДАТАМ (СЛЕВА) */}
        <Grid item xs={12} md={3}>
          <Typography variant="h6" gutterBottom sx={{ color: "text.primary" }}>
            {translations["Archive"]}
          </Typography>
          <Paper elevation={2}>
            <List component="nav" dense>
              {/* Кнопка "Все новости" */}
              <ListItemButton
                selected={selectedFilter === null}
                onClick={() => setSelectedFilter(null)}>
                <ListItemText primary={translations["AllNews"]} />
              </ListItemButton>

              {/* Рендерим кнопки фильтров по месяцам */}
              {dateFilters.map(monthIso => (
                <ListItemButton
                  key={monthIso}
                  selected={selectedFilter === monthIso}
                  onClick={() => setSelectedFilter(monthIso)}>
                  {/* Используем нашу утилиту для красоты */}
                  <ListItemText primary={formatMonthYear(monthIso, lang)} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* КОЛОНКА 2: СЕТКА НОВОСТЕЙ (СПРАВА) */}
        <Grid item xs={12} md={9}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 10 }}>
              <CircularProgress />
            </Box>
          ) : (
            // Твой Grid2 теперь находится здесь и использует 'filteredNews'
            <Grid
              container
              spacing={4}
              justifyContent="center"
              alignItems="stretch">
              {filteredNews.length > 0 ? (
                filteredNews.map(newsItem => (
                  <Grid item key={newsItem.id} xs={12} sm={6} md={4}>
                    {" "}
                    {/* Используем Grid v5 item */}
                    <StyledCard>
                      <Box sx={{ position: "relative" }}>
                        <StyledCardMedia
                          component="img"
                          // 8. ИЗОБРАЖЕНИЕ: Берем первое из массива Base64
                          image={
                            newsItem.images && newsItem.images.length > 0
                              ? `data:;base64,${newsItem.images[0]}`
                              : "/images/placeholder.jpg" // Запасная картинка, если у новости нет фото
                          }
                          alt={newsItem.title_ru} // Alt всегда ru для SEO
                        />
                        <GradientOverlay />
                      </Box>
                      <CardContent>
                        {/* 9. МУЛЬТИЯЗЫЧНЫЙ ТЕКСТ */}
                        <Typography variant="h6" component="div" gutterBottom>
                          {truncateText(
                            lang === "kz"
                              ? newsItem.title_kz
                              : newsItem.title_ru,
                            30,
                          )}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 1,
                            height: 35,
                            overflow: "hidden" /* Ограничим высоту описания */,
                          }}>
                          {truncateText(
                            lang === "kz"
                              ? newsItem.description_kz
                              : newsItem.description_ru,
                            50,
                          )}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mt: 2,
                          }}>
                          <StyledButton
                            variant="contained"
                            onClick={() => navigate(`/news/${newsItem.id}`)} // <-- ДОБАВЬ ЭТО
                          >
                            {translations["Read more"]}
                          </StyledButton>
                          {/* 10. ФОРМАТИРОВАНИЕ ДАТЫ */}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textWrap: "wrap" }}>
                            {new Date(newsItem.public_date).toLocaleDateString(
                              "ru-RU",
                            )}
                          </Typography>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                ))
              ) : (
                <Typography sx={{ p: 5, color: "text.secondary" }}>
                  {translations["no_news_found"] ||
                    "По этому фильтру новостей не найдено."}
                </Typography>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Container>
  );
}
